import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AboutUs.css';
import RegistrationModal from './RegistrationModal';
import photoA from '../assets/p1.jpeg';
import photoB from '../assets/p2.jpeg';
import photoC from '../assets/p3.jpeg';
import photoD from '../assets/p4.jpeg';
import photoE from '../assets/p5.jpeg';
import feedTheNeedy from '../assets/FeedTheNeedy.jpeg';
import foodDonation from '../assets/Food_Donation.webp';
import houseOfMercy from '../assets/Image001.webp';
import groceries from '../assets/groceries3.webp';

const AboutUs = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(1);
  const totalSlides = 9;
  const slideIntervalRef = useRef(null);
  const touchStartX = useRef(null);

  const openModal = useCallback((projectName) => {
    setSelectedProject({ name: projectName });
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedProject(null);
  }, []);

  const startSlideshow = useCallback(() => {
    if (slideIntervalRef.current) clearInterval(slideIntervalRef.current);
    slideIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev % totalSlides) + 1);
    }, 5000);
  }, [totalSlides]);

  useEffect(() => {
    startSlideshow();
    return () => {
      if (slideIntervalRef.current) clearInterval(slideIntervalRef.current);
    };
  }, [startSlideshow]);

  const handleSlideChange = (slideNumber) => {
    setCurrentSlide(slideNumber);
    startSlideshow(); // Reset interval on manual change
  };

  const handleNext = () => {
    handleSlideChange((currentSlide % totalSlides) + 1);
  };

  const handlePrev = () => {
    handleSlideChange(currentSlide === 1 ? totalSlides : currentSlide - 1);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartX.current) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;

    if (Math.abs(diff) > 50) {
      if (diff > 0) handleNext();
      else handlePrev();
    }
    touchStartX.current = null;
  };

  return (
    <>
      <section className="about-us-section" id="about">
        <div className="container">
          <div className="about-grid">
            <div className="about-text-content">
              <h2>Our Story</h2>
              <div className="about-paragraphs">
                <p>
                  Being Volunteer USA was conceptualized in 2017 by professionals who were already working in the field of voluntary engagement in Health and Wellness and Youth Leadership Training.
                </p>
                <p>
                  We found that while many desired to help, volunteerism often lacked long-term sustainability and capacity building. At the heart of our initiative is a platform where Volunteers, NPOs, and Corporates collaborate to bring unique value to the social services sector.
                </p>
                <p>
                  Founded on October 6th, 2017, we believe that <strong>“Every human being has an innate desire to do something for another being.”</strong> This Individual Social Responsibility is the core of our philosophy.
                </p>
                <p>
                  We aim to develop a culture where volunteers become positive change catalysts, building a nation where characters from all walks of life work together to make a beautiful world.
                </p>
              </div>
            </div>
            <div className="about-image-card">
              <div className="decorative-blob"></div>
              <img src={feedTheNeedy} alt="Our Volunteers in Action" loading="lazy" width="600" height="400" />
              <div className="impact-badge">
                <span className="badge-number">9+</span>
                <span className="badge-text">Years of Impact</span>
              </div>
            </div>
          </div>
        </div>

        <section className='vision-mission-section' id='what-we-do'>
          <div className="container">
            <div className="vision-mission-grid">
              <div className="vm-card vision-card">
                <h3>Our Vision</h3>
                <p>
                  To develop a culture of volunteering where individual volunteers become positive change catalysts for society.
                </p>
              </div>
              <div className="vm-card mission-card">
                <h3>Our Mission</h3>
                <p>
                  To build a nation where both the extremes of our society work together to make a beautiful world to live in. To merge as a platform for CSR, Volunteers, and NGOs.
                </p>
              </div>
            </div>
          </div>
        </section>
      </section>

      <section className="media-section" id="media">
        <div className="container">
          <h3>Community Gallery</h3>
          <div 
            className="modern-slideshow"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {[feedTheNeedy, foodDonation, houseOfMercy, groceries, photoD, photoC, photoB, photoA, photoE].map((photo, index) => {
              const slideNum = index + 1;
              return (
                <div 
                  key={slideNum}
                  className={`slide-item ${currentSlide === slideNum ? 'active' : ''}`}
                >
                  <img src={photo} alt={`Community Slide ${slideNum}`} loading="lazy" width="1200" height="800" />
                  <div className="slide-overlay">
                    <span className="slide-caption">Empowering Communities</span>
                  </div>
                </div>
              );
            })}

            <button className="slide-nav prev" onClick={handlePrev} aria-label="Previous slide">‹</button>
            <button className="slide-nav next" onClick={handleNext} aria-label="Next slide">›</button>

            <div className="nav-dots" role="tablist">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                <button
                  key={num}
                  className={`nav-dot ${currentSlide === num ? 'active' : ''}`}
                  onClick={() => handleSlideChange(num)}
                  aria-label={`Go to slide ${num}`}
                  aria-selected={currentSlide === num}
                  role="tab"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="projects-section" id="projects">
        <div className="container">
          <h3>Explore Our Projects</h3>
          <div className="projects-grid">
            <div className="project-card feed-the-needy">
              <div className="project-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path>
                </svg>
              </div>
              <h4>Feed The Needy</h4>
              <p>BVUSA provides hot meals every Sunday for up to 100 people at Lowell Transitional Living Center and Lawrence House of Mercy.</p>
              <button className="register-button" onClick={() => openModal('Feed The Needy')}>Get Involved</button>
            </div>
            <div className="project-card donation-drive">
              <div className="project-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.38 3.46L16 2a8 8 0 01-8 0L3.62 3.46a2 2 0 00-1.34 2.23l.58 3.47a1 1 0 00.99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 002-2V10h2.15a1 1 0 00.99-.84l.58-3.47a2 2 0 00-1.34-2.23z"></path>
                </svg>
              </div>
              <h4>Quarterly Donation Drive</h4>
              <p>Collecting gently used clothing quarterly and providing new clothes and jackets during Thanksgiving and Christmas.</p>
              <button className="register-button" onClick={() => openModal('Quarterly Donation Drive')}>Donate Items</button>
            </div>
          </div>
        </div>
      </section>

      {isModalOpen && <RegistrationModal onClose={closeModal} selectedProject={selectedProject} />}
    </>
  );
};

export default AboutUs;
